<template>
  <Breadcrumb :items="breadcrumbs" />
  <Loading v-if="loading" />
  <div v-else class="form-page">
    <div class="form-page-header">
      <div class="form-page-header__icon">
        <i class="pi pi-file-edit"></i>
      </div>
      <div class="form-page-header__text">
        <h1 class="form-page-header__title">تعديل مقال</h1>
        <p class="form-page-header__subtitle">{{ title }}</p>
      </div>
    </div>

    <div class="container-fluid">
      <form @submit.prevent="sendForm()">
        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-sitemap"></i></div>
            <span class="form-card__title">التصنيف</span>
          </div>
          <div class="form-card__body">
            <div class="row g-4">
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label">القسم <span class="optional">اختياري</span></label>
                  <Dropdown v-model="category" :options="categories" showClear optionLabel="title" placeholder="اختر القسم" class="w-100" />
                </div>
              </div>
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label">الرابط المختصر (Slug)</label>
                  <InputText v-model="slug" class="w-100" dir="ltr" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-pencil"></i></div>
            <span class="form-card__title">تفاصيل المقال</span>
          </div>
          <div class="form-card__body">
            <div class="row g-4">
              <div class="col-12">
                <div class="form-field">
                  <label class="field-label">عنوان المقال <span class="required">*</span></label>
                  <InputText v-model="title" class="w-100" />
                  <span class="field-error" v-if="v$.title.$error">
                    <i class="pi pi-exclamation-circle"></i> برجاء ادخال عنوان المقال
                  </span>
                </div>
              </div>
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label">الكاتب <span class="optional">اختياري</span></label>
                  <InputText v-model="authorName" class="w-100" placeholder="اسم الكاتب" />
                </div>
              </div>
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label">الوسوم (Tags) <span class="optional">اختياري — افصل بينها بفاصلة</span></label>
                  <InputText v-model="tagsInput" class="w-100" placeholder="كيمياء, ثانوية عامة, مراجعة" />
                </div>
              </div>
              <div class="col-12">
                <div class="form-field">
                  <label class="field-label">ملخص المقال <span class="optional">اختياري</span></label>
                  <Textarea v-model="excerpt" rows="2" class="w-100" />
                </div>
              </div>
              <div class="col-12">
                <div class="form-field">
                  <label class="field-label">محتوى المقال <span class="required">*</span></label>
                  <Editor v-model="content" editorStyle="height: 300px" />
                  <span class="field-error" v-if="v$.content.$error">
                    <i class="pi pi-exclamation-circle"></i> برجاء ادخال محتوى المقال
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-image"></i></div>
            <span class="form-card__title">الصورة المميزة</span>
          </div>
          <div class="form-card__body">
            <img v-if="currentImageLink && !featuredImageFile" :src="currentImageLink" alt="" class="current-image-preview mb-3" />
            <div :class="['upload-zone', { 'upload-zone--has-file': featuredImageFile }]">
              <div class="upload-zone__icon">
                <i :class="`pi ${featuredImageFile ? 'pi-check' : 'pi-cloud-upload'}`"></i>
              </div>
              <p class="upload-zone__label">{{ featuredImageFile ? featuredImageFile.name : 'اضغط لاختيار صورة جديدة' }}</p>
              <p class="upload-zone__hint" v-if="!featuredImageFile">PNG, JPG, GIF</p>
              <input type="file" @change="onFeaturedImageChange" accept="image/*" />
            </div>
          </div>
        </div>

        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-cog"></i></div>
            <span class="form-card__title">الإعدادات</span>
          </div>
          <div class="form-card__body">
            <div class="source-toggle">
              <i class="pi pi-eye" style="color: var(--text-secondary);"></i>
              <span class="source-toggle__label">نشر المقال</span>
              <InputSwitch v-model="isPublished" />
              <span class="source-toggle__hint">{{ isPublished ? 'ظاهر في المدونة العامة' : 'مسودة غير ظاهرة' }}</span>
            </div>
            <div class="source-toggle mt-3">
              <i class="pi pi-star-fill" style="color: var(--text-secondary);"></i>
              <span class="source-toggle__label">مقال مميز</span>
              <InputSwitch v-model="isFeatured" />
              <span class="source-toggle__hint">{{ isFeatured ? 'ضمن المقالات المميزة' : 'مقال عادي' }}</span>
            </div>
          </div>
        </div>

        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-search"></i></div>
            <span class="form-card__title">تحسين محركات البحث (SEO)</span>
          </div>
          <div class="form-card__body">
            <div class="row g-4">
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label">عنوان SEO <span class="optional">اختياري</span></label>
                  <InputText v-model="metaTitle" class="w-100" />
                </div>
              </div>
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label">وصف SEO <span class="optional">اختياري</span></label>
                  <Textarea v-model="metaDescription" rows="2" class="w-100" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-actions">
          <button type="submit" class="btn-submit" :disabled="formSending">
            <i class="pi pi-spin pi-spinner-dotted" v-if="formSending"></i>
            <i class="pi pi-check" v-else></i>
            {{ formSending ? 'جاري الحفظ...' : 'حفظ التعديلات' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Dropdown from 'primevue/dropdown';
import InputSwitch from 'primevue/inputswitch';
import Editor from 'primevue/editor';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';
import { useBlogStore } from '@/stores/useBlogStore.js';

export default {
  name: 'BlogEdit',
  components: { Breadcrumb, Loading, InputText, Textarea, Dropdown, InputSwitch, Editor },
  data() {
    return {
      v$: useVuelidate(),
      postId: this.$route.params.id,
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      authorName: '',
      tagsInput: '',
      category: null,
      categories: [],
      featuredImageFile: null,
      currentImageLink: null,
      isPublished: false,
      isFeatured: false,
      metaTitle: '',
      metaDescription: '',
      formSending: false,
      loading: true,
    };
  },
  validations() {
    return {
      title: { required },
      content: { required },
    };
  },
  computed: {
    store() { return useBlogStore(); },
    breadcrumbs() {
      return [
        { name: 'الرئيسية', path: '/' },
        { name: 'المدونة', path: '/blog' },
        { name: 'تعديل مقال', path: `/blog/edit/${this.postId}` },
      ];
    },
  },
  methods: {
    onFeaturedImageChange(e) {
      this.featuredImageFile = e.target.files[0] || null;
    },
    async fetchCategories() {
      try {
        const { data } = await axios.get('/admin/blog-categories');
        this.categories = data.data ?? [];
      } catch { /* non-fatal */ }
    },
    async fetchPost() {
      this.loading = true;
      try {
        const post = await this.store.fetchPost(this.postId);
        this.title = post.title ?? '';
        this.slug = post.slug ?? '';
        this.excerpt = post.excerpt ?? '';
        this.content = post.content ?? '';
        this.authorName = post.author_name ?? '';
        this.tagsInput = (post.tags ?? []).join(', ');
        this.category = post.category ?? null;
        this.currentImageLink = post.featured_image_link ?? null;
        this.isPublished = !!post.is_published;
        this.isFeatured = !!post.is_featured;
        this.metaTitle = post.meta_title ?? '';
        this.metaDescription = post.meta_description ?? '';
      } catch (error) {
        Swal.fire({ icon: 'error', title: error.response?.data?.message || 'تعذر تحميل المقال', timer: 2500, showConfirmButton: false });
      } finally {
        this.loading = false;
      }
    },
    buildFormData() {
      const fd = new FormData();
      fd.append('title', this.title.trim());
      fd.append('slug', this.slug.trim());
      fd.append('excerpt', this.excerpt ?? '');
      fd.append('content', this.content ?? '');
      fd.append('author_name', this.authorName ?? '');
      const tags = this.tagsInput.split(',').map((t) => t.trim()).filter(Boolean);
      if (tags.length) {
        tags.forEach((tag) => fd.append('tags[]', tag));
      } else {
        // Send a single empty scalar (not tags[]) so a cleared tag list actually
        // clears server-side — the app's ConvertEmptyStringsToNull middleware
        // turns this into null, which passes `nullable|array` and is included
        // in $request->validated(), unlike an omitted key (which update() would
        // just leave untouched).
        fd.append('tags', '');
      }
      fd.append('blog_category_id', this.category ? this.category.id : '');
      fd.append('is_published', this.isPublished ? 1 : 0);
      fd.append('is_featured', this.isFeatured ? 1 : 0);
      fd.append('meta_title', this.metaTitle ?? '');
      fd.append('meta_description', this.metaDescription ?? '');
      if (this.featuredImageFile) fd.append('featured_image', this.featuredImageFile);
      return fd;
    },
    async sendForm() {
      this.v$.$validate();
      if (this.v$.$error) return;
      this.formSending = true;
      try {
        await this.store.updatePost(this.postId, this.buildFormData());
        Swal.fire({ icon: 'success', title: 'تم حفظ التعديلات بنجاح', timer: 1500, showConfirmButton: false });
        this.$router.push('/blog');
      } catch (error) {
        Swal.fire({ icon: 'error', title: error.response?.data?.message || 'حدث خطأ', timer: 2500, showConfirmButton: false });
      } finally {
        this.formSending = false;
      }
    },
  },
  async mounted() {
    await this.fetchCategories();
    await this.fetchPost();
  },
};
</script>

<style scoped>
.current-image-preview { max-width: 240px; border-radius: var(--radius-md); border: 1px solid var(--surface-border); display: block; }
</style>
